/**
 * ClarificationBanner - json-render component
 *
 * Displays when the AI needs clarification from the user.
 * Shows compact action buttons only - no free-text input.
 */

import { Loader2, MessageSquareMore } from 'lucide-react';
import { Button, CardContent, CardFooter, CardHeader, CardTitle } from '@pagespeedify/ui';
import MarkdownRenderer from './MarkdownRenderer';
import type { ClarificationActionMode } from '../../types/json-render';

export interface ClarificationOption {
    label: string;
    value: string;
    variant?: 'default' | 'outline' | 'secondary' | 'destructive';
    actionId?: string;
    expiresAt?: string;
    disabled?: boolean;
    disabledReason?: string;
}

export interface ClarificationBannerProps {
    question: string;
    options?: ClarificationOption[];
    timestamp?: string;
    onSubmit?: (option: ClarificationOption) => void;
    isLoading?: boolean;
    loadingActionId?: string;
    activeActionIds?: string[];
    forceDisabled?: boolean;
    disabledReason?: string;
    actionMode?: ClarificationActionMode;
}

export default function ClarificationBanner({
    question,
    options,
    timestamp,
    onSubmit,
    isLoading = false,
    loadingActionId,
    activeActionIds = [],
    forceDisabled = false,
    disabledReason,
    actionMode = 'full'
}: ClarificationBannerProps) {
    const hasOptions = options && options.length > 0;
    const activeSet = new Set(activeActionIds);
    const now = Date.now();

    const normalizeOptionLabel = (option: ClarificationOption): string => {
        const rawLabel = String(option.label || '').trim();
        if (!rawLabel) return rawLabel;
        return rawLabel
            .replace(/\s*\([^)]*\)\s*$/i, '')
            .replace(/\s*[\u2022\u00b7\u2027]\s*.*$/i, '')
            .trim();
    };

    return (
        <div className="bg-amber-50/50 dark:bg-amber-950/20 overflow-hidden">
            <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                    <MessageSquareMore className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <CardTitle className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                        Clarification Needed
                    </CardTitle>
                </div>
            </CardHeader>
            <CardContent className="pb-3">
                <MarkdownRenderer
                    content={question}
                    className="prose-p:text-sm prose-p:mb-0 prose-p:text-amber-900 dark:prose-p:text-amber-100 prose-strong:text-amber-900 dark:prose-strong:text-amber-100"
                />
                {timestamp && (
                    <p className="mt-2 text-xs text-amber-600/70 dark:text-amber-400/70">
                        Asked {new Date(timestamp).toLocaleTimeString()}
                    </p>
                )}
            </CardContent>
            {onSubmit && hasOptions && actionMode === 'full' && (
                <CardFooter className="pt-0 pb-3">
                    <div className="flex flex-wrap gap-2">
                        {options.map((opt, index) => {
                            const expiresAtMs = opt.expiresAt ? new Date(opt.expiresAt).getTime() : Number.NaN;
                            const isExpired = Number.isFinite(expiresAtMs) && expiresAtMs <= now;
                            const isInactive = !!opt.actionId && activeSet.size > 0 && !activeSet.has(opt.actionId);
                            const disabled = forceDisabled || isLoading || opt.disabled === true || isInactive || isExpired;
                            const isClickedAction = !!loadingActionId && !!opt.actionId && loadingActionId === opt.actionId;

                            return (
                                <Button
                                    key={`${opt.actionId || opt.value}-${index}`}
                                    variant={opt.variant || 'outline'}
                                    size="sm"
                                    onClick={() => {
                                        if (disabled) return;
                                        onSubmit(opt);
                                    }}
                                    disabled={disabled}
                                    title={
                                        forceDisabled
                                            ? (disabledReason || 'Action is no longer available.')
                                            : (
                                                opt.disabled
                                                    ? (opt.disabledReason || 'This action is currently unavailable.')
                                                    : ((isInactive || isExpired) ? 'Updating options...' : undefined)
                                            )
                                    }
                                    className="text-xs h-7 px-3"
                                >
                                    {isClickedAction ? (
                                        <>
                                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                            Processing...
                                        </>
                                    ) : (
                                        normalizeOptionLabel(opt)
                                    )}
                                </Button>
                            );
                        })}
                    </div>
                </CardFooter>
            )}
        </div>
    );
}
